<template>
  <ValidationObserver
    ref="appObserver"
    :class="$style.noOverflow"
    slim
  >
    <AsidePanel
      ref="aside"
      bodyContentClassName="aside-container"
      :class="[
        $style.aside,
        {[$style.noOverflowX]: hasPopover},
      ]"
      :components="asideTabs"
      :disableCta="isProcessingForm"
      :handleCancel="cancelClick"
      :handleMountedCallback="handleMountedCallback"
      :handleSave="handleSave"
      :initialTabIndex="initialTabIndex"
      :isExpanded="isExpanded"
      :itemId="itemId"
      :itemType="getItemType"
      :navigateToTab="activeTab"
      :noNotification="true"
      :overrideBodyNavItems="$style.noOverflowX"
      :saveText="btnSaveOrActivateLabel"
      :viewport="viewport"
    >
      <template v-slot:avatar>
        <ApplicationAvatar />
      </template>

      <template v-slot:leftPaneCustomContent>
        <ApplicationProtipBanner />
      </template>

      <template v-slot:status>
        <ApplicationStatus
          v-if="isItemBeingUpdated"
          :commitHasFailed="commitHasFailed"
          :handleError="saveError"
        />
      </template>

      <template v-slot:footerCustomContent>
        <OidcConfigConfirmationModal
          v-if="showOidcConfirmationModal"
          :clientID="oidcConfigResponse.clientId"
          :clientSecret="oidcConfigResponse.clientSecret"
          @close="closeOidcModal"
        />
      </template>
    </AsidePanel>
  </ValidationObserver>
</template>

<script>
/* eslint-disable max-len */

import {
  IDENTITY_MANAGEMENT_LABEL,
  IDM_OPERATIONS,
  OAUTH_SESSION_STORAGE_KEY,
  PENDING_OAUTH_CONFIG_UPDATE,
  PROVISION_STATUS_AUTHORIZED,
  applicationModelFields,
  applicationModelNames,
} from 'client/util/Constants/ApplicationConstants';
import {
  cloneDeep,
  extend,
  get,
  isEmpty,
  isEqual,
  startCase,
  values,
} from 'lodash';
import { isOIDC } from 'client/applications/utils/ApplicationUtils';

import { ValidationObserver } from 'vee-validate3';
import { addValidationRule } from '@jumpcloud/ui-components';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { notify } from 'client/util';
import { onboardingTasks } from 'client/GrowthEngineering/utilities/GrowthConstants';
import ApplicationAvatar from 'client/applications/ApplicationAvatar.vue';
import ApplicationGeneralInfoConfiguration from 'client/applications/ApplicationGeneralInfoConfiguration.vue';
import ApplicationIdmConfiguration from 'client/applications/scim/ApplicationIdmConfiguration.vue';
import ApplicationProtipBanner from 'client/applications/ApplicationProtipBanner/ApplicationProtipBanner.vue';
import ApplicationSSOConfiguration from 'client/applications/ApplicationSSOConfiguration.vue';
import ApplicationStatus from 'client/applications/ApplicationStatus.vue';
import ApplicationUsersGroups from 'client/applications/ApplicationUsersGroups.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import Constants, {
  ItemTypeRoutes, StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import HardcodedScimSupportMixin from 'client/applications/mixins/HardcodedScimSupportMixin';
import IdentityManagementMixin, { SCIMPanelEventManager } from 'client/applications/mixins/IdentityManagementMixin';
import ItemTypeConstants from 'client/util/Constants/ItemTypeConstants';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import OidcConfigConfirmationModal from 'client/applications/oidc/OidcConfigConfirmationModal';
import RouteGuardMixin from 'client/AdminPortal/router/guards/RouteGuardMixin';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SessionStorageService from 'client/util/SessionStorageService';
import Util from 'client/util/Util';
import navigationTools from 'client/util/PageNavigationUtil';

const { CONFIG, DATABASE_ATTRIBUTES } = applicationModelFields;
const { ItemTypeConsts } = ItemTypeConstants;
const { OIDC } = applicationModelNames;
const { application, bookmark } = ItemTypeConsts;
const applicationCollection = StoreModuleMap[application];

const applicationModel = StoreModelMap[application];
const onboardingModule = 'OnboardingModule';
const {
  error,
  success,
} = Constants;
const { ACTIVATE, UPDATE } = IDM_OPERATIONS;

// This constant has to be in sync with computed asideTabs
// index of the identityManagementTab object in the returned array
const IDENTITY_MANAGEMENT_TAB_INDEX = 2;

export default {
  name: 'ApplicationAsidePanel',

  components: {
    ApplicationAvatar,
    ApplicationProtipBanner,
    ApplicationStatus,
    AsidePanel,
    OidcConfigConfirmationModal,
    ValidationObserver,
  },

  mixins: [
    AsidePanelPropsMixin,
    HardcodedScimSupportMixin,
    IdentityManagementMixin,
    RouteGuardMixin,
  ],

  defaultValidationMessage: 'Could not save application. Please complete all the required information.',

  props: {
    provisionActivationStatus: {
      type: String,
      default: '',
    },
    templateId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      areScimUpdatesDetected: false,
      commitHasFailed: false,
      commitFromScimPanel: false,
      initialTabIndex: 0,
      isConfirmationModalClosed: false,
      itemHasBeenSaved: false,
      activeTab: undefined,
      parentType: ItemTypeConsts.application,
      /**
       * _pendingIdmNotificationsQueue_ is a temp "queue" to save IdM notifications.
       * This will be registered on the store during handleMountedCallback
       * This is because a REST_MODEL is called before it, so registering
       * notifications before thehandledCallback will be cleared.
       */
      pendingIdmNotificationsQueue: [],
      processingForm: false,
      scimConnectorType: '',
      shouldInitSCIMActivationFlow: false,
      showOidcConfirmationModal: false,
      validationErrorMessage: this.$options.defaultValidationMessage,
    };
  },

  computed: {
    ...mapState(applicationModel, [
      'isDefaultLogoRestored',
      'item',
      'oidcConfigResponse',
      'pendingIdmNotifications',
      'previousItem',
      'template',
      'scimRedirectLocation',
      'scimNeedsCredentials',
    ]),

    ...mapGetters(applicationModel, [
      'areScimCredentialsChanged',
      'didTranslationRulesChanged',
      'isItemBeingCreated',
      'isItemBeingUpdated',
      'isScimGenericConnectorChanged',
      'isScimGroupsEnabledChanged',
    ]),

    /**
     * This computed property will check on item properties
     * and define which tabs will be loaded
     */
    asideTabs() {
      const userGroupsTab = {
        label: 'User Groups',
        vueComponent: ApplicationUsersGroups,
        vueComponentProps: {
          itemId: this.itemId,
          parentType: ItemTypeConsts.application,
        },
      };

      const generalInfoTab = {
        vueComponent: ApplicationGeneralInfoConfiguration,
        label: 'General Info',
      };

      const ssoTab = {
        label: this.item.isBookmark ? 'Bookmark' : 'SSO',
        vueComponent: ApplicationSSOConfiguration,
      };

      const identityManagementTab = {
        label: IDENTITY_MANAGEMENT_LABEL,
        vueComponent: ApplicationIdmConfiguration,
      };

      /**
       * This if statement will avoid aside panel to show all 4 tabs and then rerender
       * to the correct arrangement of tabs
       */
      if (this.item.name.length === 0) {
        return [];
      }

      const hideScimTab = !this.showIdentityManagementTab()
        && this.isAppExcluded();

      if (hideScimTab) {
        return [generalInfoTab, ssoTab, userGroupsTab];
      }

      return [generalInfoTab, ssoTab, identityManagementTab, userGroupsTab];
    },

    btnSaveOrActivateLabel() {
      return this.isItemBeingCreated ? 'activate' : 'save';
    },

    getItemType() {
      const applicationWorkflow = this.$hasFeatureFlag(this.$featureFlags.applicationWorkflow);
      const ssoOrApp = applicationWorkflow
        ? ItemTypeConsts.application
        : ItemTypeConsts.sso;
      return this.item.isBookmark ? ItemTypeConsts.bookmark
        : ssoOrApp;
    },

    hasPopover() {
      return this.asideTabs.some(tab => !!tab.vueComponent);
    },

    isProcessingForm() {
      return this.processingForm;
    },
  },

  beforeCreate() {
    const collection = applicationCollection;

    extend(
      this.$options.methods,
      mapActions(collection, [
        'refreshCollection',
      ]),
    );

    addValidationRule('url', {
      getMessage(field) {
        return `${field} must be a valid URL`;
      },

      validate(value) {
        return Util.isValidUrl(value);
      },
    });

    addValidationRule('url-secure', {
      getMessage(field) {
        return `${field} must be a valid, secure URL`;
      },

      validate(value) {
        return Util.isValidUrl(value, true);
      },
    });
  },

  created() {
    this.setApplicationLogo(null);
    this.setIsDefaultLogoRestored(false);
    this.setTemplateId(this.templateId);
  },

  mounted() {
    SCIMPanelEventManager.$on('showScimModal', (data = {}) => {
      const { scimConnectorType = '' } = data;
      this.handleShowScimModal(scimConnectorType);
    });

    SCIMPanelEventManager.$on('scimInactive', () => {
      this.handleSupportedScimAppDeactivated();
    });

    SCIMPanelEventManager.$on('scimCredentialsValid', () => {
      this.cancelClick();
    });

    this.handleProvisionStatus();
  },

  updated() {
    this.updatePreviousItem(cloneDeep(this.item));
  },

  beforeDestroy() {
    SCIMPanelEventManager.$off();
    // clear item state so previous app is not visible when changing apps
    this.setItem({});
  },

  methods: {
    ...mapActions(applicationModel, [
      'applicationModelUpdate',
      'bindApplicationToUserGroups',
      'activateScim',
      'configureScim',
      'deleteAppCustomLogo',
      'registerPendingIdmNotification',
      'resetState',
      'saveItem',
      'setApplicationLogo',
      'setIsDefaultLogoRestored',
      'setItem',
      'setItemField',
      'setOidcClientIdAndSecret',
      'setPreviousItemField',
      'setTemplateId',
      'unset',
      'updateConfigureScim',
      'updatePreviousItem',
      'uploadAppCustomLogo',
    ]),

    ...mapActions(applicationCollection, [
      'refreshCollection',
    ]),

    ...mapActions(onboardingModule, [
      'completeOnboardingTask',
    ]),
    ...mapActions(notificationModule, [
      'triggerNotification',
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    autoGenerateCert(item, isAutoGenerate) {
      let data = {};
      if (isAutoGenerate && !item.isBookmark) {
        const certName = 'certificate.pem';
        const url = window.URL.createObjectURL(
          new Blob([atob(item.config.idpCertificate.value)], { type: 'application/pem-certificate-chain' }, certName),
        );
        data = {
          certName,
          url,
        };
      }
      return data;
    },

    canAutoGenerate() {
      if (this.item.isBookmark || isOIDC(this.item)) {
        return false;
      }

      return this.isItemBeingCreated
        && this.item.config.idpCertificate && !this.item.config.idpCertificate.value;
    },

    cancelClick(e) {
      if (e) e.preventDefault();
      this.closeAside();
    },

    closeAside() {
      navigationTools.setLocation(ItemTypeRoutes[this.itemType]);
      this.refreshStore();
    },

    closeOidcModal() {
      this.showOidcConfirmationModal = false;
      this.setOidcClientIdAndSecret();
      this.closeAside();
    },

    confirmSave(handleCancel, asidePanel, onError) {
      this.commitHasFailed = false;
      const flowType = this.commitFromScimPanel ? 'SCIM' : 'SAML';
      // if it's new, warn the user and then save the ssoURL
      if (this.isItemBeingCreated) {
        if (flowType === 'SCIM') {
          return this.showSCIMModal(asidePanel, onError, handleCancel);
        }
        if (isOIDC(this.item)) {
          return Promise.resolve(true);
        }
        return this.showSAMLModal(asidePanel, onError);
      }

      const scimGenericChanges = !this.isPrebuild
        && this.isScimActive
        && (this.isScimGenericConnectorChanged
        || this.areScimCredentialsChanged);
      if (scimGenericChanges) {
        return this.showScimGenericConectorChangesModal();
      }

      return Promise.resolve(true);
    },

    enqueueNotification(notification) {
      this.pendingIdmNotificationsQueue.push(notification);
    },

    /** By now we need to get the application details from the Application Config AsidePanel.js,
     *  in other to access the vue $validators of the dynamic controls
     *  to force the validation of them when saving or deactivating an app */
    getApplicationDetails() {
      return this.$refs.aside.$children.find(x => x.isApplicationDetails);
    },

    getApplicationTypeLabel() {
      return this.item.isBookmark ? startCase(bookmark) : startCase(application);
    },

    getApplicationTypeLabelLowerCase() {
      return this.getApplicationTypeLabel().toLowerCase();
    },

    /**
     * Registers custom OAuth redirect errors
     * @param oauthPendingOperation 'activate' | 'update'
     */
    getCustomOauthErrorNotification(oauthPendingOperation) {
      let messageType;
      switch (oauthPendingOperation) {
        case ACTIVATE:
          messageType = 'AUTH_FAILURE';
          break;
        case UPDATE:
          messageType = 'UPDATE_FAILURE';
          break;
        default:
          break;
      }

      return messageType;
    },

    getMsgApplicationHasBeenDeactivated() {
      return `${this.getApplicationTypeLabel()} has been deactivated.`;
    },

    getMsgCouldNotSaveApplication() {
      return `Could not save ${this.getApplicationTypeLabelLowerCase()}`;
    },

    /**
     * Helper function to detect if there is an ongoing Custom Idm OAuth
     * transaction with this app and what kind of operation it is: activate or update
     */
    getOauthOperation() {
      const { itemId: id } = this;
      const oauthActivation = SessionStorageService.getItem(`${OAUTH_SESSION_STORAGE_KEY}-${id}`);
      const oauthUpdate = SessionStorageService.getItem(`${PENDING_OAUTH_CONFIG_UPDATE}-${id}`);
      if (oauthActivation) {
        return ACTIVATE;
      }

      if (oauthUpdate) {
        return UPDATE;
      }

      return null;
    },

    /**
    * This method is about decoupling the UI template model from the actual SSO values.
    * So know, will be building the application-aside UI based on the template information,
    * and just getting the values from the instances.
    * That would allow us to eventually avoid store UI information into SSO instances,
    * also would allow us to notify users about new templates configurations
    * without the need of updating the instances.
    */
    getUIModel() {
      // When is new we do not have to merge the template with the instance
      if (this.isItemBeingCreated) {
        return this.item;
      }

      const overrideGroupsAttributeNameTooltip = (templateModel) => {
        const { config } = templateModel;
        const {
          isBookmark,
          sso: { type },
        } = this.item;

        if (!isBookmark && type !== OIDC && config.groupsAttributeName) {
          config.groupsAttributeName.tooltip = null;
        }

        return templateModel;
      };

      // get base model from template
      const ssoTemplate = overrideGroupsAttributeNameTooltip(cloneDeep(this.template));
      const keyPairValues = cloneDeep(this.item);
      const isObjectProperty = prop => prop && typeof prop !== 'string' && Object.keys(prop).length;

      const mergeObjects = (base, override) => {
        // Null is a valid value that should be respected by instances
        if (override === null) {
          return null;
        }

        const original = cloneDeep(base);
        const instance = cloneDeep(override);

        if (Array.isArray(instance)) return instance;

        Object.keys(original).forEach((key) => {
          if (instance === undefined) return;

          const keyInformation = original[key];
          const incomingInfo = instance[key];

          if (incomingInfo === undefined) return;
          if (key === DATABASE_ATTRIBUTES) return;

          if (isObjectProperty(keyInformation)) {
            if (CONFIG === key) {
              Object.keys(keyInformation).forEach((configKey) => {
                const configInfo = keyInformation[configKey];
                const incomingConfigInfo = incomingInfo[configKey];

                if (!incomingConfigInfo) return;
                configInfo.value = incomingConfigInfo.value;
                delete incomingInfo[configKey];
              });

              original[CONFIG] = {
                ...incomingInfo, // add instance props that are not on the template.
                ...keyInformation,
              };
            } else {
              original[key] = mergeObjects(keyInformation, incomingInfo);
            }
          } else {
            original[key] = incomingInfo;
          }

          delete instance[key];
        });

        return {
          ...instance, // add instance props that are not on the template.
          ...original,
        };
      };

      return mergeObjects(ssoTemplate, keyPairValues);
    },

    handleMountedCallback() {
      const model = this.getUIModel();

      Object.entries(model)
        .forEach(([key, value]) => this.setItemField({ key, value }));
      Object.entries(model)
        .forEach(([key, value]) => this.setPreviousItemField({ key, value }));

      this.handlePendingNotifications();
    },

    handlePendingNotifications() {
      // Flush notifications on component's data to application model (store)
      this.pendingIdmNotificationsQueue.forEach(notificationConfig => this.registerPendingIdmNotification(notificationConfig));

      if (this.pendingIdmNotifications.length > 0) {
        this.navigateToIdmTab();
      }
    },

    handleProvisionStatus() {
      const { provisionActivationStatus: provisionStatus } = this;

      if (provisionStatus === '') { return; }

      const oauthPendingOperation = this.getOauthOperation();

      // provisionStatus is either 'success' or 'error'
      const notifConfig = { type: provisionStatus };

      if (oauthPendingOperation) {
        switch (provisionStatus) {
          case PROVISION_STATUS_AUTHORIZED:
            if (oauthPendingOperation === ACTIVATE) {
              // Update is handled by itself
              notifConfig.type = success; // type has to be success, else "authorized" and it is not valid
              notifConfig.messageType = 'AUTH_SUCCESS';
            }
            break;

          case error:
            notifConfig.messageType = this.getCustomOauthErrorNotification(oauthPendingOperation);
            break;

          default:
            break;
        }
      } else {
        switch (provisionStatus) {
          case success:
            notifConfig.messageType = 'SCIM_SUCCESS';
            break;
          case error:
            notifConfig.messageType = 'SCIM_ERROR';
            break;
          default:
            break;
        }
      }

      this.enqueueNotification(notifConfig);
      this.setIdmTabAsInitialTabIndex();
    },

    async handleSave() {
      const isV3Valid = await this.$refs.appObserver.validate();

      if (!isV3Valid) {
        throw Error(this.validationErrorMessage);
      }

      await this.$validator.validate();

      let canContinue = await this.confirmSave();

      if (this.isConfirmationModalClosed) {
        return new SaveReturnValue({ canContinue });
      }

      if (canContinue) {
        this.handleValidationErrors();
      } else {
        this.handleValidationErrors();
        return new SaveReturnValue({ canContinue });
      }

      if (this.isItemBeingCreated) {
        this.setItemField({ key: 'active', value: true });
      }

      try {
        this.processingForm = true;
        if (!this.item.isBookmark) {
          const ssoUrl = this.item.ssoDirName + this.item.ssoFileName;
          this.setItemField({ key: 'ssoUrl', value: ssoUrl });
        } else {
          this.unset({ key: 'config' });
        }

        await this.deleteAppCustomLogo();
        const isNewConnection = this.isItemBeingCreated;
        const isAutoGenerate = this.canAutoGenerate();
        const item = await this.saveItem();

        this.itemHasBeenSaved = true;

        if (isNewConnection && isOIDC(item)) {
          this.showOidcModal();
        }

        const prebuildScimNeedsUpdate = this.isScimActive
          && this.isPrebuild
          && (this.isScimGroupsEnabledChanged || this.didTranslationRulesChanged);
        if (prebuildScimNeedsUpdate) {
          await this.updateConfigureScim(item.id);
        }

        await this.bindApplicationToUserGroups();
        const certData = this.autoGenerateCert(item, isAutoGenerate);
        await this.saveSuccess(item);

        if (certData.url) {
          this.triggerNotification({
            title: 'Public Certificate has been created',
            component: {
              template: `<a download="${certData.certName}" href="${certData.url}"><i class="jc-actions-csvdownload"></i>&nbsp;&nbsp;Download Certificate</a>`,
            },
            showCloseButton: true,
          });
        }

        await this.uploadAppCustomLogo();
        await this.setItem({ item });

        let isRedirected = false;
        if (this.shouldInitSCIMActivationFlow) {
          canContinue = false;
          try {
            isRedirected = await this.initSCIMActivationFlow();
          } catch (err) {
            this.processingForm = false;
            isRedirected = false;
            this.saveError(err);
          }
        }

        if (!isRedirected) {
          await this.refreshCollection({ item });
          canContinue = true;
        }
      } catch (err) {
        this.processingForm = false;
        canContinue = false;
        this.saveError(err);
      }

      if (this.showOidcConfirmationModal) {
        canContinue = false;
      }
      return new SaveReturnValue({ canContinue });
    },

    handleShowScimModal(scimConnectorType = '') {
      this.commitFromScimPanel = true;
      this.scimConnectorType = scimConnectorType;
      this.$refs.aside.handleItemSave();
    },

    handleSupportedScimAppDeactivated() {
      const [mainPanel] = this.viewport.mainPanel.vm.$children;
      mainPanel.$refs.itemTable.refresh();
    },

    handleValidationErrors() {
      if (!this.isValid() || this.$validator.errors.any()) {
        throw Error(this.validationErrorMessage);
      }
    },

    hasModelChanged() {
      return this.itemHasBeenSaved ? false : this.shouldShowUnsavedModal();
    },

    hideawsIdm() {
      const { name } = this.item;
      if (this.item.name === 'aws' && this.isScimActive) return true;
      if (this.item.name === 'aws' && !this.isScimActive) return false;
      return !!this.item.provision || this.hardcodedIsScimSupported(name);
    },

    async initSCIMActivationFlow() {
      let isRedirected = false;
      try {
        await this.configureScim(this.item.id);

        if (this.scimConnectorType === 'generic') {
          await this.activateScim();
          notify(success, 'Identity Management integration has been successfully verified.');
        }

        if (this.scimRedirectLocation !== '') {
          isRedirected = true;
          notify(success, 'Redirecting to SP login page');
          navigationTools.setLocation(this.scimRedirectLocation);
        }
      } catch (responseError) {
        notify(error, 'There was a problem activating Identity Management.');
      }
      return isRedirected;
    },

    isAppExcluded() {
      const { name } = this.item;
      return this.isAppIdentityManagementExcluded(name);
    },

    isValid() {
      const errorMessages = [];

      // Loop through all of the dynamic config items and perform validation...
      // eslint-disable-next-line
      for (const key in this.item.config) {
        const configItem = cloneDeep(this.item.config[key]);

        // eslint-disable-next-line no-continue
        if (configItem === null) continue;
        // eslint-disable-next-line no-useless-escape
        const configItemName = configItem.label.replace(/\:$/, '');

        if (configItem.dependsOnProperty) {
          const parentItem = cloneDeep(this.item.config[configItem.dependsOnProperty]);
          // eslint-disable-next-line no-useless-escape
          const parentItemName = parentItem.label.replace(/\:$/, '');

          if (parentItem.value) {
            // Value required if parent property true (otherwise value ignored)
            if (isEmpty(configItem.value)) {
              errorMessages[configItem.position] = `${configItemName} is required when '${parentItemName}' is checked.`;
            }
          }
          // dependentOnProperty and required mutually exclusive so continue
          // eslint-disable-next-line no-continue
          continue;
        }
        // TODO: Remove second condition when feature flag is removed
        if (configItem.label === 'SAMLSubject NameID:'
            && this.$hasFeatureFlag(this.$featureFlags.ssoCustomSamlSubjectNameId)) configItem.type = 'customDropdown';

        if (configItem.required) {
          const emptyMessage = `${configItemName} required for application configuration.`;

          if (configItem.type === 'text' || configItem.type === 'customDropdown') {
            if (isEmpty(configItem.value)) {
              errorMessages[configItem.position] = emptyMessage;
            }
          } else if (configItem.type === 'file') {
            const certificateKeys = {
              idpCertificate: 1,
              idpPrivateKey: 1,
            };
            const isCertificateKey = !!certificateKeys[key];
            const throwEmptyCertificateAlert = isCertificateKey
              && this.certOrKeyHasChanged && isEmpty(configItem.value);
            const throwEmptyAlert = this.isItemBeingCreated
              && isEmpty(configItem.value) && !this.certOrKeyHasChanged && !isCertificateKey;

            if (throwEmptyCertificateAlert || throwEmptyAlert) {
              errorMessages[configItem.position] = emptyMessage;
            }
          } else if (configItem.type === 'constantAttributes') {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < configItem.value.length; i++) {
              const entry = configItem.value[i];
              if (entry.required && (isEmpty(entry.name) || isEmpty(entry.value))) {
                errorMessages[configItem.position] = emptyMessage;
                break;
              }
            }
          } else if (configItem.type === 'select') {
            let addErrorMessage = true;

            if (Array.isArray(configItem.options)) {
              configItem.options.forEach((option) => {
                if (typeof option === 'object' && option.text === configItem.value) {
                  addErrorMessage = false;
                } else if (option === configItem.value) {
                  addErrorMessage = false;
                }
              });
            } else if (values(configItem.options).indexOf(configItem.value) !== -1) {
              addErrorMessage = false;
            }

            if (addErrorMessage) {
              errorMessages[configItem.position] = emptyMessage;
            }
          }
        }
      }

      // If we have some error messages, display the first one (we do this to make sure
      // we're validating from top to bottom by .position)...
      let valid = true;
      let errorMessage = '';
      if (errorMessages.length > 0) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < errorMessages.length; i++) {
          if (!isEmpty(errorMessages[i])) {
            errorMessage = errorMessages[i];
            valid = false;
            break;
          }
        }
      }

      if (!this.item.displayLabel.trim()) {
        return false;
      }

      this.validationErrorMessage = errorMessage || this.$options.defaultValidationMessage;

      return valid;
    },

    navigateToIdmTab() {
      this.activeTab = { tabLabel: IDENTITY_MANAGEMENT_LABEL };
    },

    refreshStore() {
      this.resetState();
    },

    saveError(res) {
      this.commitHasFailed = true;
      if (res && res instanceof Error) {
        notify(error, res.message);
        return;
      }
      if (res && typeof res.error === 'string') {
        notify(error, res.error);
        return;
      }
      if (res && res.responseJSON && typeof res.responseJSON.error === 'string') {
        notify(error, res.responseJSON.error);
        return;
      }
      notify(error, this.getMsgCouldNotSaveApplication());
    },

    saveSuccess(item) {
      const { triggerApplicationAdded } = EventHelper;

      if (item.active) {
        this.triggerSuccessNotification({
          title: 'Saved',
          message: 'Application has been saved.',
        });
      } else {
        this.triggerNotification({
          title: 'Saved',
          message: this.getMsgApplicationHasBeenDeactivated(),
        });
      }
      // Trigger Optimizely events
      const resourceName = get(item, 'name', '');
      const _id = get(item, '_id', '');
      const id = get(item, 'id', '');
      const name = get(item, 'config.acsUrl.value', '');
      const props = {
        isNewApplication: !!_id && !!id,
        name,
        resourceName,
      };
      triggerApplicationAdded(props);
      this.completeOnboardingTask(onboardingTasks.discoverResources);
      return Promise.resolve(item);
    },

    scimActivationError() {
      notify(error, 'Identity Management integration failed. Please try again.');
    },

    scimActivationSuccess() {
      notify(success, 'Identity Management integration has been successfully verified');
    },

    setIdmTabAsInitialTabIndex() {
      // This method is ran during mounted so asideTabs does not have
      // the tab objects yet. They are loaded after the initialTabIndex prop has
      // been passed to the AsidePanel. But by that time, the AsidePanel will show the index 0 tab
      // This is to select the Idm tab as soon as the AsidePanel is rendered
      this.initialTabIndex = IDENTITY_MANAGEMENT_TAB_INDEX;
    },

    shouldShowUnsavedModal() {
      return !isEqual(this.item, this.previousItem);
    },

    showIdentityManagementTab() {
      const { name } = this.item;
      const isBetaFeatureActive = this.hasBetaFeature(name) && !this.isBetaflagActive(name);
      if (isBetaFeatureActive) return false;
      const isScimSupported = this.hideawsIdm();
      return isScimSupported;
    },

    showOidcModal() {
      this.showOidcConfirmationModal = true;
    },

    showSAMLModal() {
      this.isConfirmationModalClosed = false;
      return new Promise((resolve) => {
        const { isBookmark } = this.item;
        if (isBookmark) {
          resolve(true);
        } else {
          ModalEventBus.open({
            component: 'ConfirmationModal',
            componentProps: {
              headerText: 'Please confirm your new SSO connector instance',
              bodyText: 'Note that the IdP URL cannot be shared across applications and this URL is not editable after creation.',
              handleCancel: () => {
                this.isConfirmationModalClosed = true;
                ModalEventBus.close();
                resolve(false);
              },
              handleContinue: () => {
                ModalEventBus.close();
                resolve(true);
              },
              showClose: true,
            },
          });
        }
      });
    },

    showSCIMModal() {
      this.isConfirmationModalClosed = false;
      return new Promise((resolve) => {
        const { isBookmark } = this.item;
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            headerText: 'Save Application',
            bodyText: `This application needs to be saved to configure an Identity Management integration. General info and Single-Sign On configurations for this application will be saved. Note that the ${isBookmark ? '' : 'IdP'} URL can't be shared across applications and this URL can't be edited after it's created.`,
            handleCancel: () => {
              this.isConfirmationModalClosed = true;
              ModalEventBus.close();
              this.commitFromScimPanel = false;
              resolve(false);
            },
            handleContinue: () => {
              ModalEventBus.close();
              this.triggerSavedApp = false;
              this.shouldInitSCIMActivationFlow = true;
              resolve(true);
            },
            showClose: true,
            cancelText: 'cancel',
            continueText: 'save',
          },
        });
      });
    },

    showScimGenericConectorChangesModal() {
      this.isConfirmationModalClosed = false;
      return new Promise((resolve) => {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            headerText: 'Changes have been detected',
            bodyText: 'Changes have been detected to your configuration. To commit these changes, please test the connection and update the settings.',
            handleCancel: () => {
              this.isConfirmationModalClosed = true;
              ModalEventBus.close();
              this.commitFromScimPanel = false;
              resolve(false);
            },
            handleContinue: () => {
              ModalEventBus.close();
              this.triggerSavedApp = false;
              this.shouldInitSCIMActivationFlow = true;
              resolve(false);
            },
            showClose: true,
            cancelText: '',
            continueText: 'okay',
          },
        });
      });
    },
  },
};
</script>

<style module>
@value ( jcGrey, jcGrey600, jcGrey700, jcSuccess ) from '~css/brand-colors.css';
/* stylelint-disable */
.aside :global(.aside-container) :global(.application-details-container) {
  overflow-x: auto;
  padding-left: var(--asideSpacing);
  top: 0 !important; /* used to rewrite jc general styles */
}

.aside :global(.aside-container) :global(.application-details-container) :global(.configuration) {
  margin-right: 55px;
}

.aside li :global(div.b-dropdown) :global(button.dropdown-toggle) {
  overflow: hidden;
  padding-right: 24px !important;
  text-overflow: ellipsis;
}

.aside :global(.configuration) :global(.dynamic-controls-attributes-header) h2 strong {
  font-size: 14px;
  font-weight: 700;
  padding-left: 0;
}

/*
Start of GROUP ATTRIBUTES CHANGES
These changes were done with CSS because implementing then on the aside
as a component would require a major refactor of the aside logic.
*/
.aside :global(.includeGroups) {
  display: inline-block;
  margin-top: 10px;
  position: relative;
}

.aside :global(.includeGroups) label span {
  display: none;
}

.aside :global(.includeGroups) label::before {
  color: jcGrey;
  content: 'GROUP ATTRIBUTES';
  display: block;
}

.aside :global(.includeGroups) input {
  display: block;
  margin-top: 15px;
}

.aside :global(.includeGroups) label::after {
  color: jcGrey;
  content: "include group attribute";
  font-size: 11px;
  left: 30px;
  position: absolute;
  top: 32.5px;
  width: 120px;
}

.aside :global(.includeGroups) :global(.icon-tooltip) {
  left: 133px;
  position: absolute;
  top: 0;
}

.aside :global(.groupsAttributeName) {
  float: right;
  margin: 0;
  margin-top: 30px;
  width: calc(100% - 160px);
}

.aside :global(.groupsAttributeName) label {
  display: none;
}

/* End of GROUP ATTRIBUTES CHANGES */

.aside :global(.dynamic-controls-attributes-header) h2 {
  font-size: 14px !important;
  font-weight: 700;
  padding-left: 0 !important;
}

.aside :global(.dynamic-controls-attributes-header) p {
  font-size: 11px;
}

.aside :global(.configuration) :global(.idp-url.idp-url) :global(.control-label.control-label) {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  line-height: 22px;
  margin: 0;
  text-transform: capitalize;
}

.aside :global(.configuration) :global(.idp-url.idp-url) :global(.idp-input.idp-input) {
  outline: none;
  width: auto;
}

.aside :global(.configuration) :global(.instance-label.instance-label) :global(.control-label.control-label),
.aside :global(.configuration) :global(.signAssertion.signAssertion) span {
  font-size: 1rem;
  font-weight: 600;
  line-height: 22px;
  margin: 0;
  text-transform: capitalize;
}

.aside :global(.secondary-dynamic-controls-container) {
  margin-bottom: 15px;
}

.aside :global(.secondary-dynamic-controls-container) :global(.dynamic-controls-double-list-box) {
  margin-bottom: 60px;
  position: relative;
}

.aside :global(.secondary-dynamic-controls-container) :global(.dynamic-controls-double-list-box) header :global(button.list-editor-add-button) {
  bottom: -40px;
  left: 3px;
  position: absolute;
}

.aside :global(.secondary-dynamic-controls-container) :global(.dynamic-controls-double-list-box) header :global(.list-editor-remove-button) {
  padding-top: 11px;
}

.aside :global(.dynamic-control-container) header,
.aside :global(.dynamic-control-container) header p {
  margin-bottom: 0;
}

.noOverflow {
  overflow: unset !important;
}
.noOverflowX {
  overflow-x: unset !important;
}
/* stylelint-enable */
</style>
